import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { AccountSettingsService } from '../account-settings.service';
import { ToastService } from '../toast/toast.service';

const PASSWORD_PATTERN = /^[\x21-\x7E]{8,50}$/;

@Component({
  selector: 'app-account-change-password',
  standalone: true,
  imports: [ReactiveFormsModule, TranslateModule],
  templateUrl: './account-change-password.component.html',
  styleUrl: '../account-change/_account-change-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AccountChangePasswordComponent {
  private readonly fb = inject(FormBuilder);
  private readonly accountSettingsService = inject(AccountSettingsService);
  private readonly dialogRef = inject(MatDialogRef<AccountChangePasswordComponent, boolean>);
  private readonly toastService = inject(ToastService);

  protected readonly isSubmitting = signal(false);
  protected readonly errorMessageKey = signal<string | null>(null);
  protected readonly form = this.fb.nonNullable.group({
    currentPassword: ['', Validators.required],
    newPassword: ['', [Validators.required, Validators.pattern(PASSWORD_PATTERN)]],
    confirmNewPassword: ['', Validators.required]
  }, { validators: matchingPasswordValidator });

  protected submit(): void {
    if (this.isSubmitting()) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSubmitting.set(true);
    this.errorMessageKey.set(null);

    this.accountSettingsService
      .changePassword({
        Password: this.form.controls.currentPassword.value,
        NewPassword: this.form.controls.newPassword.value,
        ConfirmNewPassword: this.form.controls.confirmNewPassword.value
      })
      .pipe(finalize(() => this.isSubmitting.set(false)))
      .subscribe({
        next: () => {
          this.toastService.showSuccess('account.changePassword.success');
          this.dialogRef.close(true);
        },
        error: () => this.errorMessageKey.set('account.changePassword.failed')
      });
  }

  protected close(): void {
    this.dialogRef.close(false);
  }
}

const matchingPasswordValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const newPassword = control.get('newPassword')?.value;
  const confirmNewPassword = control.get('confirmNewPassword')?.value;

  return newPassword === confirmNewPassword ? null : { passwordMismatch: true };
};
